import { useTranslations } from '@/i18n/use-translations';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { Controller, useForm, useFieldArray, useWatch } from 'react-hook-form';
import { VisuallyHiddenInput } from '@/components/common/visually-hidden.input';
import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useImagePreview } from '@/hooks/use-image-preview';
import { ImagePreview } from '@/components/common/image-preview';
import type { GetEventSettingsDto } from '@/types/dtos/events/response/get-event-settings.dto';
import type { SponsorUploadDto } from '@/types/dtos/events/request/update-event-settings.dto';

interface FormSponsor extends SponsorUploadDto {
  isDeleted?: boolean;
}

interface UpdateEventSponsorsForm {
  sponsors: FormSponsor[];
}

export default function SponsorsForm({
  onSettingsChange,
  initialSettings,
}: {
  onSettingsChange: (settings: UpdateEventSponsorsForm) => void;
  initialSettings?: GetEventSettingsDto;
}) {
  const t = useTranslations();

  // Store original sponsors for change detection
  const [originalSponsors, setOriginalSponsors] = useState<GetEventSettingsDto['sponsors']>([]);
  // Track image previews
  const { createPreview, removePreview, getPreview } = useImagePreview();

  const form = useForm<UpdateEventSponsorsForm>({
    defaultValues: {
      sponsors: [],
    },
  });

  // Update form when initial settings change
  useEffect(() => {
    if (initialSettings?.sponsors) {
      // Store original sponsors for comparison
      queueMicrotask(() => setOriginalSponsors(initialSettings.sponsors));

      // Convert DTO sponsors to form sponsors (without image files, just names and IDs)
      const formSponsors = initialSettings.sponsors.map((sponsor) => ({
        id: sponsor.id,
        name: sponsor.name || '',
        image: undefined, // Always undefined since we can't convert URL to File
        isDeleted: false,
      }));
      form.reset({
        sponsors: formSponsors,
      });
    }
  }, [initialSettings, form]);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'sponsors',
  });

  // Function to mark a sponsor for deletion
  const markSponsorForDeletion = (index: number) => {
    // Clean up preview URL if it exists
    removePreview(`sponsor-${index}`);
    form.setValue(`sponsors.${index}.isDeleted`, true);
  };

  // Helper function to detect changes and return only changed sponsors
  const getChangedSponsors = useCallback(
    (currentSponsors: FormSponsor[]): SponsorUploadDto[] => {
      const changedSponsors: SponsorUploadDto[] = [];

      currentSponsors.forEach((currentSponsor) => {
        // Handle deleted sponsors
        if (currentSponsor.isDeleted && currentSponsor.id) {
          changedSponsors.push({
            id: currentSponsor.id,
            delete: true,
          });
          return;
        }

        // Skip deleted sponsors that don't need to be sent to backend
        if (currentSponsor.isDeleted) {
          return;
        }

        // New sponsor (no ID)
        if (!currentSponsor.id) {
          // Only include if it has a name or image
          if (currentSponsor.name || currentSponsor.image) {
            changedSponsors.push({
              name: currentSponsor.name,
              image: currentSponsor.image,
            });
          }
          return;
        }

        // Existing sponsor - find original by ID to check for changes
        const originalSponsor = originalSponsors?.find((orig) => orig.id === currentSponsor.id);
        const nameChanged = currentSponsor.name !== originalSponsor?.name;
        const imageChanged = !!currentSponsor.image; // If there's a new file, it's changed

        if (nameChanged || imageChanged) {
          changedSponsors.push({
            id: currentSponsor.id,
            name: currentSponsor.name,
            image: currentSponsor.image,
          });
        }
      });

      return changedSponsors;
    },
    [originalSponsors],
  );

  // Notify parent of form changes with only changed sponsors
  const sponsorsValue = useWatch({ control: form.control, name: 'sponsors' });
  useEffect(() => {
    if (!sponsorsValue) return;

    const currentSponsors: FormSponsor[] = sponsorsValue.filter((sponsor): sponsor is FormSponsor => sponsor != null);

    const changedSponsors = getChangedSponsors(currentSponsors);

    // Send the changed sponsors in the expected format
    onSettingsChange({
      sponsors: changedSponsors,
    });
  }, [sponsorsValue, onSettingsChange, getChangedSponsors]);

  const addSponsor = () => {
    if (fields.length < 5) {
      append({ name: '', image: undefined, isDeleted: false });
    }
  };

  const handleFileUpload = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    createPreview(`sponsor-${index}`, file);
    form.setValue(`sponsors.${index}.image`, file || undefined);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">{t('event.detail.settingsMenu.sponsors.title')}</Typography>

      <Stack gap={2}>
        {fields.map((field, index) => {
          const sponsor = sponsorsValue?.[index];
          // Hide deleted sponsors from UI
          if (sponsor?.isDeleted) return null;

          return (
            <Box
              key={field.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
              }}
            >
              <Stack gap={1}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="subtitle2">
                    {t('event.detail.settingsMenu.sponsors.sponsor')} {index + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      if (sponsor?.id) {
                        // Existing sponsor - mark for deletion
                        markSponsorForDeletion(index);
                      } else {
                        // New sponsor - clean up URL and remove from form
                        removePreview(`sponsor-${index}`);
                        remove(index);
                      }
                    }}
                    sx={{ ml: 'auto' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>

                <Controller
                  name={`sponsors.${index}.name`}
                  control={form.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label={t('event.detail.settingsMenu.sponsors.name')}
                      placeholder={t('event.detail.settingsMenu.sponsors.namePlaceholder')}
                    />
                  )}
                />

                <Stack gap={1} alignItems="start">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => document.getElementById(`sponsor-image-${index}`)?.click()}
                  >
                    {sponsorsValue?.[index]?.image || initialSettings?.sponsors?.[index]?.imageUrl
                      ? t('event.detail.settingsMenu.sponsors.changeImage')
                      : t('event.detail.settingsMenu.sponsors.selectImage')}
                  </Button>
                  <VisuallyHiddenInput
                    id={`sponsor-image-${index}`}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(index, e)}
                  />

                  <ImagePreview
                    file={sponsorsValue?.[index]?.image}
                    previewUrl={getPreview(`sponsor-${index}`)}
                    existingImageUrl={initialSettings?.sponsors?.[index]?.imageUrl}
                    alt={`${t('event.detail.settingsMenu.sponsors.sponsor')} ${index + 1}`}
                    width={150}
                    height={100}
                  />
                </Stack>
              </Stack>
            </Box>
          );
        })}

        {fields.filter((_, index) => !sponsorsValue?.[index]?.isDeleted).length < 5 && (
          <Button startIcon={<AddIcon />} variant="outlined" onClick={addSponsor} sx={{ alignSelf: 'flex-start' }}>
            {t('event.detail.settingsMenu.sponsors.addSponsor')}
          </Button>
        )}

        {fields.filter((_, index) => !sponsorsValue?.[index]?.isDeleted).length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {t('event.detail.settingsMenu.sponsors.noSponsors')}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
